import { Box, Center, Spinner, useToast } from "@chakra-ui/react"
import { ActionCreatorWithPayload } from "@reduxjs/toolkit"
import NewPostForm from "components/Organismos/PostForm"
import FormModalTemplate from "components/Templates/FormModal"
import { parsePostForm, PostForm } from "models/post.model"
import { useEffect } from "react"
import { useAppDispatch, useTypedSelector } from "store/hooks"
import { createPostRequest } from "store/modules/posts/actions/createPost"
import { cleanPostData, getPostByIdRequest } from "store/modules/posts/actions/getPostById"
import { getPostsPaginatedRequest } from "store/modules/posts/actions/getPostsPaginated"
import { updatePostRequest } from "store/modules/posts/actions/updatePost"
import { useDispatchToPromise } from "utils/dispatch-to-promise"
import { useQueryParam } from "utils/hooks/useQueryParam"
import { wait } from "utils/wait"

const NewPostsPage = () => {
    const [showPostForm, setShowPostForm] = useQueryParam<boolean>('showPostForm');
    const [postId, setPostId] = useQueryParam<string>('postId');
    const [postType, setPostType] = useQueryParam<"PONTO_ESSENCIAL" | "INFORMATIVO">('post_type');
    const dispatch = useAppDispatch();
    const dispatchAsPromise = useDispatchToPromise();
    const {
        isRequesting: isLoadingData,
        successPayload: postData
    } = useTypedSelector(state => state.posts.getPostById);

    const closeNewPostModal = async () => {
        setShowPostForm(false);
        await wait(200);
        setPostId('');
        dispatch(cleanPostData());
    }

    const toast = useToast();

    const handleFormSubmit = async (data: PostForm) => {
        const isEditing = !!postId;
        const createPost = parsePostForm[data.type === "1" ? "INFORMATIVO" : "PONTO_ESSENCIAL"];
        const request: ActionCreatorWithPayload<any> = isEditing ? updatePostRequest : createPostRequest;
        try {
            await dispatchAsPromise(request, {
                postType: data.type === "1" ? "INFORMATIVO" : "PONTO_ESSENCIAL",
                postId: postId ?? '',
                post: createPost(data)
            });
            toast({
                status: 'success',
                description: `Postagem ${isEditing ? 'atualizada' : 'criada'} com sucesso!`,
            });
            dispatch(getPostsPaginatedRequest({
                postType: data.type === "1" ? "INFORMATIVO" : "PONTO_ESSENCIAL",
                index: 0,
                pageSize: 10,
                onlyPublished: false
            }))
            closeNewPostModal();
        } catch (err) {
            toast({
                status: 'error',
                description: 'Houve um problema ao criar esta postagem.',
                containerStyle: {
                    zIndex: 9999999
                }
            });
        }
    }

    useEffect(() => {
        if (postId && postType) {
            dispatch(getPostByIdRequest({ postId, postType }))
        }
    }, [dispatch, postId, postType]);

    useEffect(() => {
        if (!postType) {
            setPostType('INFORMATIVO');
        }
    }, []);

    return (
        <Box>
            <FormModalTemplate
                form={(
                    <>
                        {isLoadingData && (
                            <Center w="full" p={6}>
                                <Spinner />
                            </Center>
                        )}
                        {!isLoadingData && <NewPostForm
                            initialData={{
                                description: postData?.descricao,
                                photo: postData?.photoAsFile,
                                title: postData?.titulo,
                                type: postType === "INFORMATIVO" ? '1' : '2',
                                startTime: postData?.horaAberturaFormatada,
                                endTime: postData?.horaFechamentoFormatada,
                                phoneNumber: postData?.telefone,
                                position: postData ? {
                                    latitude: postData.latitude,
                                    longitude: postData.longitude,
                                } : undefined,
                                address: postData?.endereco ? {
                                    zipcode: postData.endereco.cep,
                                    streetType: postData.endereco.tipoLogradouro,
                                    streetName: postData.endereco.logradouro,
                                    neighborhood: postData.endereco.bairro,
                                    addressNumber: postData.endereco.numero,
                                    city: postData.endereco.municipio,
                                    state: postData.endereco.uf,
                                    addressComplement: postData.endereco.complemento
                                } : undefined
                            }}
                            onSuccess={handleFormSubmit}
                        />}
                    </>
                )}
                isOpenModal={String(showPostForm) === 'true'}
                onCloseModal={closeNewPostModal}
            />
        </Box>
    )
}

export default NewPostsPage